import { apiClient } from './api';
import { API_CONFIG } from '@/config/api.config';

const N = (API_CONFIG as any).ENDPOINTS.NIRP;

export const incidentService = {
  list: (params?: any) => apiClient.get(N.INCIDENTS, { params }),
  get: (id: string) => apiClient.get(N.INCIDENT_DETAIL(id)),
  create: (data: any) => apiClient.post(N.INCIDENTS, data),
  update: (id: string, data: any) => apiClient.patch(N.INCIDENT_DETAIL(id), data),
  delete: (id: string) => apiClient.delete(N.INCIDENT_DETAIL(id)),
  updateStatus: (id: string, status: string) => apiClient.post(N.INCIDENT_STATUS(id), { status }),
  uploadAttachment: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post(N.INCIDENT_ATTACHMENT(id), form);
  },
  bulkImport: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post(N.INCIDENT_BULK_IMPORT, form);
  },
  dashboardSummary: (params?: any) => apiClient.get(N.INCIDENT_DASHBOARD, { params }),
  statistics: (params?: any) => apiClient.get(N.INCIDENT_STATS, { params }),
  securityOverview: (params?: any) => apiClient.get(N.INCIDENT_OVERVIEW, { params }),
  formChoices: () => apiClient.get(N.INCIDENT_FORM_CHOICES),
  /** Full-field CSV (attack vector, MITRE, analysis, IOCs, etc.) honoring the same filters as list() — unlike list(), which only returns IncidentListSerializer's thin field set. */
  exportCsv: (params?: any) => apiClient.get<Blob>(N.INCIDENT_EXPORT_CSV, { params, responseType: 'blob' }),
};

export const threatIntelService = {
  listDistributions: (params?: any) => apiClient.get(N.INTEL, { params }),
  createDistribution: (data: any) => apiClient.post(N.INTEL, data),
  distribute: (id: string, orgIds: string[]) =>
    apiClient.post(N.INTEL_DISTRIBUTE(id), { organization_ids: orgIds }),
  acknowledge: (id: string) => apiClient.post(N.INTEL_ACKNOWLEDGE(id), {}),
  listIndicators: (params?: any) => apiClient.get(N.INTEL_INDICATORS, { params }),
  createIndicator: (data: any) => apiClient.post(N.INTEL_INDICATORS, data),
  listIncidentIocs: () => apiClient.get(N.INTEL_INCIDENT_IOCS),
  promoteIocs: (ioc_ids: string[]) => apiClient.post(N.INTEL_PROMOTE, { ioc_ids }),
  listSharedThreatReports: () => apiClient.get<any[]>('/incidents/threat-sharing/'),
  listExternalShares: () => apiClient.get<any[]>('/incidents/threat-sharing/'),
  markShareRead: (shareId: string) => apiClient.post(`/incidents/threat-sharing/${shareId}/mark-read/`, {}),
};

// Kibo-authored threat sharing (create → review → approve → share)
const AUTHORED_BASE = '/incidents/threat-shares/';

export const authoredShareService = {
  list: (params?: any) => apiClient.get<any[]>(AUTHORED_BASE, { params }),
  get: (id: string) => apiClient.get<any>(`${AUTHORED_BASE}${id}/`),
  create: (data: any) => apiClient.post<any>(AUTHORED_BASE, data),
  update: (id: string, data: any) => apiClient.patch<any>(`${AUTHORED_BASE}${id}/`, data),
  remove: (id: string) => apiClient.delete(`${AUTHORED_BASE}${id}/`),
  submit: (id: string) => apiClient.post<any>(`${AUTHORED_BASE}${id}/submit/`, {}),
  approve: (id: string, reviewNotes?: string) =>
    apiClient.post<any>(`${AUTHORED_BASE}${id}/approve/`, { review_notes: reviewNotes ?? '' }),
  sendBack: (id: string, reviewNotes: string) =>
    apiClient.post<any>(`${AUTHORED_BASE}${id}/send-back/`, { review_notes: reviewNotes }),
  share: (id: string, config: { shared_fields?: string[] }) =>
    apiClient.post<any>(`${AUTHORED_BASE}${id}/share/`, config),
};

export const complianceService = {
  listFrameworks: () => apiClient.get(N.FRAMEWORKS),
  listScores: (params?: any) => apiClient.get(N.SCORES, { params }),
  createScore: (data: any) => apiClient.post(N.SCORES, data),
  summary: () => apiClient.get(N.SCORES_SUMMARY),
  listFindings: (params?: any) => apiClient.get(N.FINDINGS, { params }),
  createFinding: (data: any) => apiClient.post(N.FINDINGS, data),
};
